import React, { useState, useEffect, useCallback, useRef } from 'react';
import { accountsApi } from '../../utils/accountsApi';
import { api } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { displayName as dn } from '../../utils/displayName';
import ChartOfAccountsCardView from './ChartOfAccountsCardView';
import ChartOfAccountsTreeView from './ChartOfAccountsTreeView';
import './ChartOfAccountsPage.css';

const highlight = (text, term) => {
    if (!term || !text || term.trim() === '') return text;
    try {
        const re = new RegExp(`(${term.trim()})`, 'gi');
        const parts = String(text).split(re);
        return parts.map((p, i) => re.test(p) ? <mark key={i} className="search-highlight">{p}</mark> : p);
    } catch {
        return text;
    }
};

const ITEMS_PER_PAGE = 20;

const emptyForm = {
    code: '',
    name: '',
    arabicName: '',
    type: 'POSTING',
    parentId: '',
    currencyId: '',
    entityId: '',
    hasCostCenter: false,
    notes: '',
    sortOrder: 0,
};

const ChartOfAccountsPage = () => {
    const { lang } = useLanguage();
    const [accounts, setAccounts] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters / sort / pagination
    const [viewMode, setViewMode] = useState('table');
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('');
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState('asc');

    // Support data for dropdowns
    const [currencies, setCurrencies] = useState([]);
    const [entities, setEntities] = useState([]);

    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selected, setSelected] = useState(null);
    const [isEdit, setIsEdit] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const scrollRef = useRef(null);

    // ── Fetch support data once ──────────────────────────
    useEffect(() => {
        Promise.all([
            api.get('/currencies'),
            api.get('/entities'),
        ]).then(([cur, ent]) => {
            setCurrencies(cur.items ?? cur);
            setEntities(ent ?? []);
        }).catch(() => {});
    }, []);

    // ── Fetch accounts ───────────────────────────────────
    const fetchAccounts = useCallback(() => {
        setLoading(true);
        setError(null);
        accountsApi.list({
            search: search || undefined,
            type: filterType || undefined,
            page,
            limit: ITEMS_PER_PAGE,
        })
            .then(res => {
                setAccounts(res.items);
                setTotal(res.total);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [search, filterType, page]);

    useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

    // ── Drag-to-scroll ───────────────────────────────────
    useEffect(() => {
        const slider = scrollRef.current;
        if (!slider) return;
        let isDown = false, startX = 0, scrollLeft = 0;
        const down = e => { isDown = true; startX = e.pageX - slider.offsetLeft; scrollLeft = slider.scrollLeft; };
        const up = () => { isDown = false; };
        const move = e => { if (!isDown) return; e.preventDefault(); slider.scrollLeft = scrollLeft - (e.pageX - slider.offsetLeft - startX) * 2; };
        slider.addEventListener('mousedown', down);
        slider.addEventListener('mouseup', up);
        slider.addEventListener('mouseleave', up);
        slider.addEventListener('mousemove', move);
        return () => {
            slider.removeEventListener('mousedown', down);
            slider.removeEventListener('mouseup', up);
            slider.removeEventListener('mouseleave', up);
            slider.removeEventListener('mousemove', move);
        };
    }, [loading]);

    // ── Local sort (on the current page) ────────────────
    const sorted = [...accounts].sort((a, b) => {
        if (!sortCol) return 0;
        const av = a[sortCol], bv = b[sortCol];
        if (av == null) return sortDir === 'asc' ? -1 : 1;
        if (bv == null) return sortDir === 'asc' ? 1 : -1;
        if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortDir === 'asc' ? av - bv : bv - av;
    });

    const handleSort = col => {
        if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        else { setSortCol(col); setSortDir('asc'); }
    };

    const sortIcon = col => sortCol !== col ? null
        : sortDir === 'asc' ? <i className="fas fa-sort-up" /> : <i className="fas fa-sort-down" />;

    const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

    // ── Modals ───────────────────────────────────────────
    const openAdd = () => {
        setForm({ ...emptyForm, currencyId: currencies[0]?.id ?? '' });
        setFormErrors({});
        setShowAddModal(true);
    };

    const closeAdd = () => { setShowAddModal(false); setForm(emptyForm); setFormErrors({}); };

    const openView = acct => { setSelected(acct); setIsEdit(false); setShowViewModal(true); };
    const closeView = () => { setShowViewModal(false); setSelected(null); setIsEdit(false); setForm(emptyForm); };

    const handleEdit = () => { setForm({ ...selected, parentId: selected.parentId ?? '', entityId: selected.entityId ?? '' }); setIsEdit(true); };

    const handleInput = e => {
        const { name, value, type, checked } = e.target;
        setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
        if (formErrors[name]) setFormErrors(f => { const n = { ...f }; delete n[name]; return n; });
    };

    const validate = () => {
        const errs = {};
        if (!form.code?.trim()) errs.code = 'Account code is required';
        if (!form.name?.trim()) errs.name = 'Account name is required';
        if (!form.currencyId) errs.currencyId = 'Currency is required';
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async e => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                code: form.code.trim(),
                name: form.name.trim(),
                arabicName: form.arabicName?.trim() || undefined,
                type: form.type,
                parentId: form.parentId || undefined,
                currencyId: form.currencyId,
                entityId: form.entityId || undefined,
                hasCostCenter: Boolean(form.hasCostCenter),
                notes: form.notes?.trim() || undefined,
                sortOrder: Number(form.sortOrder) || 0,
            };
            if (isEdit) {
                await accountsApi.update(selected.id, payload);
            } else {
                await accountsApi.create(payload);
            }
            closeAdd();
            closeView();
            setPage(1);
            fetchAccounts();
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async id => {
        if (!window.confirm('Deactivate this account? It will be hidden from all views.')) return;
        try {
            await accountsApi.remove(id);
            closeView();
            fetchAccounts();
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    // ── Render ───────────────────────────────────────────
    const renderViewToggle = () => (
        <div className="view-mode-buttons">
            {[['table', 'fa-table', 'Table'], ['tree', 'fa-sitemap', 'Tree'], ['card', 'fa-th-large', 'Cards']].map(([mode, icon, label]) => (
                <button key={mode} className={`view-mode-btn ${viewMode === mode ? 'active' : ''}`} onClick={() => setViewMode(mode)}>
                    <i className={`fas ${icon}`} /><span>{label}</span>
                </button>
            ))}
        </div>
    );

    const renderForm = () => (
        <form onSubmit={handleSubmit} className="account-form">
            <div className="form-grid">
                <div className="form-group">
                    <label>Account Code <span className="required">*</span></label>
                    <input name="code" value={form.code} onChange={handleInput} disabled={isEdit} className={formErrors.code ? 'error' : ''} />
                    {formErrors.code && <span className="error-message">{formErrors.code}</span>}
                </div>

                <div className="form-group">
                    <label>English Name <span className="required">*</span></label>
                    <input name="name" value={form.name} onChange={handleInput} className={formErrors.name ? 'error' : ''} />
                    {formErrors.name && <span className="error-message">{formErrors.name}</span>}
                </div>

                <div className="form-group">
                    <label>Arabic Name</label>
                    <input name="arabicName" value={form.arabicName} onChange={handleInput} dir="rtl" />
                </div>

                <div className="form-group">
                    <label>Type</label>
                    <select name="type" value={form.type} onChange={handleInput}>
                        <option value="POSTING">Posting (leaf — holds balances)</option>
                        <option value="HEADER">Header (group — no direct postings)</option>
                    </select>
                </div>

                <div className="form-group">
                    <label>Currency <span className="required">*</span></label>
                    <select name="currencyId" value={form.currencyId} onChange={handleInput} className={formErrors.currencyId ? 'error' : ''}>
                        <option value="">-- Select --</option>
                        {currencies.map(c => <option key={c.id} value={c.id}>{c.code} – {c.name}</option>)}
                    </select>
                    {formErrors.currencyId && <span className="error-message">{formErrors.currencyId}</span>}
                </div>

                <div className="form-group">
                    <label>Parent Account</label>
                    <select name="parentId" value={form.parentId} onChange={handleInput}>
                        <option value="">-- Root (no parent) --</option>
                        {accounts.filter(a => a.id !== selected?.id && a.type === 'HEADER').map(a => (
                            <option key={a.id} value={a.id}>{a.code} – {dn(a, lang)}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Sub-Entity (optional)</label>
                    <select name="entityId" value={form.entityId} onChange={handleInput}>
                        <option value="">Shared (all entities)</option>
                        {entities.map(e => <option key={e.id} value={e.id}>{e.code} – {e.name}</option>)}
                    </select>
                </div>

                <div className="form-group">
                    <label>Sort Order</label>
                    <input type="number" name="sortOrder" value={form.sortOrder} onChange={handleInput} min="0" />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Notes</label>
                    <input name="notes" value={form.notes} onChange={handleInput} />
                </div>

                <div className="form-group checkbox-group">
                    <label>
                        <input type="checkbox" name="hasCostCenter" checked={Boolean(form.hasCostCenter)} onChange={handleInput} />
                        Has Cost Center
                    </label>
                </div>
            </div>

            <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={isEdit ? () => setIsEdit(false) : closeAdd}>Cancel</button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                    <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                    {submitting ? ' Saving…' : isEdit ? ' Save Changes' : ' Add Account'}
                </button>
            </div>
        </form>
    );

    return (
        <div className="chart-of-accounts-page">
            <div className="page-header">
                <div className="page-header-left">
                    <h1 className="page-title">Chart of Accounts</h1>
                </div>
                <div className="page-header-actions">
                    <div className="view-mode-toggle">
                        <span className="view-mode-label">View:</span>
                        {renderViewToggle()}
                    </div>
                    <button className="btn-add-account" onClick={openAdd}>
                        <i className="fas fa-plus" /> Add New Account
                    </button>
                </div>
            </div>

            <div className="content-wrapper">
                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner" /><p>Loading accounts…</p>
                    </div>
                ) : error ? (
                    <div className="error-message"><i className="fas fa-exclamation-circle" /> {error}</div>
                ) : viewMode === 'card' ? (
                    <div className="card-view-wrapper">
                        <ChartOfAccountsCardView accounts={accounts} onEdit={acct => { openView(acct); }} onDelete={handleDelete} />
                    </div>
                ) : viewMode === 'tree' ? (
                    <div className="tree-view-wrapper">
                        <ChartOfAccountsTreeView embedded />
                    </div>
                ) : (
                    <>
                        <div className="controls-container">
                            <div className="search-bar">
                                <input
                                    type="text"
                                    placeholder="Search code, name, Arabic name…"
                                    value={search}
                                    onChange={e => { setSearch(e.target.value); setPage(1); }}
                                    className="search-input"
                                />
                                {search && (
                                    <button className="clear-search" onClick={() => { setSearch(''); setPage(1); }}>×</button>
                                )}
                                <i className="fas fa-search search-icon" />
                            </div>

                            <div className="filters-container">
                                <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }} className="filter-select">
                                    <option value="">All Types</option>
                                    <option value="HEADER">Header</option>
                                    <option value="POSTING">Posting</option>
                                </select>
                            </div>
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination-controls top-pagination">
                                <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}>Prev</button>
                                <span>Page {page} of {totalPages} ({total} accounts)</span>
                                <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}>Next</button>
                            </div>
                        )}

                        <div className="table-responsive" ref={scrollRef}>
                            <div className="accounts-table-wrapper">
                                <table className="accounts-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th onClick={() => handleSort('code')}>Code {sortIcon('code')}</th>
                                            <th onClick={() => handleSort('arabicName')}>Arabic Name {sortIcon('arabicName')}</th>
                                            <th onClick={() => handleSort('name')}>English Name {sortIcon('name')}</th>
                                            <th onClick={() => handleSort('type')}>Type {sortIcon('type')}</th>
                                            <th>Currency</th>
                                            <th>Parent</th>
                                            <th>Entity</th>
                                            <th onClick={() => handleSort('level')}>Level {sortIcon('level')}</th>
                                            <th onClick={() => handleSort('hasCostCenter')}>Cost Center {sortIcon('hasCostCenter')}</th>
                                            <th onClick={() => handleSort('updatedAt')}>Updated {sortIcon('updatedAt')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sorted.map((acct, idx) => (
                                            <tr key={acct.id} onClick={() => openView(acct)} style={{ cursor: 'pointer' }}>
                                                <td>{(page - 1) * ITEMS_PER_PAGE + idx + 1}</td>
                                                <td>{highlight(acct.code, search)}</td>
                                                <td dir="rtl">{highlight(acct.arabicName, search)}</td>
                                                <td>{highlight(acct.name, search)}</td>
                                                <td>
                                                    <span className={`badge badge-${acct.type === 'HEADER' ? 'header' : 'posting'}`}>
                                                        {acct.type}
                                                    </span>
                                                </td>
                                                <td>{acct.currency?.code}</td>
                                                <td>{acct.parent ? `${acct.parent.code} – ${dn(acct.parent, lang)}` : <em>Root</em>}</td>
                                                <td>{acct.entity?.code ?? <em>Shared</em>}</td>
                                                <td>{acct.level}</td>
                                                <td>{acct.hasCostCenter ? 'Yes' : 'No'}</td>
                                                <td>{new Date(acct.updatedAt).toLocaleDateString()}</td>
                                            </tr>
                                        ))}
                                        {sorted.length === 0 && (
                                            <tr><td colSpan={11} style={{ textAlign: 'center', padding: '2rem' }}>No accounts found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination-controls bottom-pagination">
                                <button onClick={() => setPage(p => Math.max(p - 1, 1))} disabled={page === 1}>Prev</button>
                                <span>Page {page} of {totalPages}</span>
                                <button onClick={() => setPage(p => Math.min(p + 1, totalPages))} disabled={page === totalPages}>Next</button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Add Account Modal ── */}
            {showAddModal && (
                <div className="modal-overlay" onClick={closeAdd}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Add New Account</h2>
                            <button className="modal-close" onClick={closeAdd}><i className="fas fa-times" /></button>
                        </div>
                        {renderForm()}
                    </div>
                </div>
            )}

            {/* ── View / Edit Account Modal ── */}
            {showViewModal && selected && (
                <div className="modal-overlay" onClick={closeView}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEdit ? 'Edit Account' : 'Account Details'}</h2>
                            <button className="modal-close" onClick={closeView}><i className="fas fa-times" /></button>
                        </div>

                        {!isEdit ? (
                            <div className="account-details">
                                <div className="details-grid">
                                    {[
                                        ['Code', selected.code],
                                        ['Arabic Name', selected.arabicName || '–'],
                                        ['English Name', selected.name],
                                        ['Type', selected.type],
                                        ['Currency', selected.currency?.code],
                                        ['Parent', selected.parent ? `${selected.parent.code} – ${dn(selected.parent, lang)}` : 'Root'],
                                        ['Entity', selected.entity?.name ?? 'Shared'],
                                        ['Level', selected.level],
                                        ['Sort Order', selected.sortOrder],
                                        ['Has Cost Center', selected.hasCostCenter ? 'Yes' : 'No'],
                                        ['Notes', selected.notes || '–'],
                                        ['Last Updated', new Date(selected.updatedAt).toLocaleString()],
                                    ].map(([label, val]) => (
                                        <div key={label} className="detail-item">
                                            <label>{label}:</label>
                                            <span>{val}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="modal-footer">
                                    <button className="btn-cancel" onClick={closeView}>Close</button>
                                    <button className="btn-danger" onClick={() => handleDelete(selected.id)}>
                                        <i className="fas fa-trash" /> Deactivate
                                    </button>
                                    <button className="btn-edit" onClick={handleEdit}>
                                        <i className="fas fa-edit" /> Edit
                                    </button>
                                </div>
                            </div>
                        ) : renderForm()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChartOfAccountsPage;
